// Page « Prestataire » : un même module, montré chez des métiers différents (nettoyage, onglerie, plomberie, coiffure),
// pour qu'un coiffeur, une prothésiste ongulaire ou un plombier s'y reconnaissent. Données de test fictives.
import type { CaptureMetier } from './vitrineMetiers';

const PRESTATIONS: CaptureMetier[] = [
    {
        src: '/vitrine/prestataire-catalogue.png', alt: 'Catalogue de prestations de nettoyage : canapés, tapis, matelas, avec prix et options', module: 'Prestations',
        titre: 'Nettoyage : vos prestations et vos prix, prêts pour le devis.',
        points: ['Une fiche par prestation : prix, durée, TVA.', 'Des options ajoutables : désinfection, protection…', 'Le client compose sa demande à partir de ce catalogue.'],
    },
    {
        src: '/vitrine/prestations-onglerie.png', alt: "Catalogue de prestations d'onglerie : pose gel, vernis semi-permanent, remplissage", module: 'Prestations',
        titre: 'Onglerie : vos poses et vos soins, avec la durée de chacun.',
        points: ["La durée de chaque pose alimente l'agenda : plus de chevauchement.", 'Vos tarifs sont affichés tels que vous les fixez.', 'Vos clientes réservent en ligne la pose de leur choix.'],
    },
    {
        src: '/vitrine/prestations-plomberie.png', alt: 'Catalogue de prestations de plomberie : fuite, débouchage, chauffe-eau, entretien', module: 'Prestations',
        titre: 'Plomberie : dépannage, installation, entretien, pour particuliers et professionnels.',
        points: ["Chaque prestation est réservée aux particuliers, aux professionnels, ou aux deux.", 'Des forfaits clairs pour que le client sache à quoi s’attendre.', 'Le contrat d’entretien annuel se propose et se signe en ligne.'],
    },
    {
        src: '/vitrine/prestations-coiffure.png', alt: 'Catalogue de prestations de coiffure : coupes, colorations, soins', module: 'Prestations',
        titre: 'Coiffure : coupes, colorations et soins, rangés par famille.',
        points: ['Les prestations sont classées en catégories : coupes, colorations, soins.', 'Une coupe et une couleur se cumulent dans un seul devis.', 'Le temps de chaque prestation décide du créneau proposé.'],
    },
];

const SIMULATEUR: CaptureMetier[] = [
    {
        src: '/vitrine/simulateur-nettoyage-A.png', alt: 'Simulateur de devis en parcours guidé : que voulez-vous faire nettoyer, avec une photo par objet', module: 'Simulateur de devis',
        titre: 'Il commence par une question simple, en images.',
        points: ['« Que voulez-vous faire nettoyer ? » : le client touche la photo qui correspond.', 'Il peut cocher plusieurs objets, un seul devis pour tout.', 'Vos catégories et vos photos, à votre image.'],
    },
    {
        src: '/vitrine/simulateur-coiffure-A.png', alt: "Simulateur de devis d'un salon de coiffure : coupe, couleur ou soin, avec une photo pour chaque choix", module: 'Simulateur de devis',
        titre: 'Même parcours pour un salon de coiffure.',
        points: ['Coupe, couleur, soin : les questions s’adaptent à votre métier.', 'L’estimation se met à jour à chaque choix.', 'Un parcours pour les particuliers, un autre pour les professionnels.'],
    },
    {
        src: '/vitrine/simulateur-coiffure-C.png', alt: "Simulateur de devis d'un salon : la coloration complète est ajoutée à l'estimation, total 79 euros", module: 'Simulateur de devis',
        titre: 'Son estimation se construit sous ses yeux.',
        points: ['Chaque choix s’ajoute à l’estimation, avec le plus et le moins.', 'Le total tient compte des quantités, TTC, sans rien calculer.', 'Un parcours pour les particuliers, un autre pour les professionnels.'],
    },
    {
        src: '/vitrine/simulateur-plomberie-A.png', alt: 'Simulateur de devis de plomberie : dépannage, installation, entretien', module: 'Simulateur de devis',
        titre: 'Du devis au rendez-vous, sans décrocher le téléphone.',
        points: ['Dépannage, installation, entretien : le client choisit son besoin.', 'Il demande son devis ou prend rendez-vous directement.', 'Les liens de paiement et les acomptes partent avec la confirmation.'],
    },
];

const AGENDA: CaptureMetier[] = [
    {
        src: '/vitrine/agenda-prestataire.png', alt: 'Agenda de la semaine d’une entreprise de nettoyage, une couleur par salarié', module: 'Planning des rendez-vous',
        titre: "Nettoyage : l'agenda de la semaine, une couleur par intervenant.",
        points: ['Qui intervient, chez qui et à quelle heure.', 'Une recherche pour retrouver un salarié.', 'Le paiement à encaisser s’affiche sur chaque rendez-vous.'],
    },
    {
        src: '/vitrine/agenda-coiffure.png', alt: 'Agenda de la semaine d’un salon de coiffure', module: 'Planning des rendez-vous',
        titre: 'Coiffure : les rendez-vous du salon, coiffeur par coiffeur.',
        points: ['Chaque coiffeur a sa couleur dans la semaine.', 'La durée de la prestation bloque le bon créneau.', 'Le client prend rendez-vous en ligne sur un créneau libre.'],
    },
    {
        src: '/vitrine/agenda-onglerie.png', alt: 'Agenda de la semaine d’une onglerie', module: 'Planning des rendez-vous',
        titre: 'Onglerie : des poses qui s’enchaînent sans chevauchement.',
        points: ['Les poses longues et les remplissages sont placés avec la bonne durée.', 'Le lien de paiement peut être envoyé pour sécuriser le rendez-vous.', 'Un rappel part au client avant son passage.'],
    },
    {
        src: '/vitrine/agenda-plomberie.png', alt: 'Agenda de la semaine d’une entreprise de plomberie', module: 'Planning des rendez-vous',
        titre: 'Plomberie : la tournée de la semaine, plombier par plombier.',
        points: ['Chaque intervention est affectée à un plombier.', 'Vous trouvez un créneau pour une urgence en un clic.', 'À la clôture de l’intervention, la facture est prête.'],
    },
];

const DOSSIERS: CaptureMetier[] = [
    {
        src: '/vitrine/prestataire-dossiers.png', alt: 'Suivi des dossiers clients de la demande à la clôture', module: 'Dossiers clients',
        titre: 'Chaque demande suivie, de la demande à la clôture.',
        points: ['Demande reçue, devis envoyé, signé, rendez-vous, réalisé.', "Ce qu'il faut faire aujourd'hui est résumé en haut.", 'Plus de demande oubliée dans une boîte mail.'],
    },
    {
        src: '/vitrine/prestataire-dossier-fiche.png', alt: 'Fiche d’un dossier client : étapes, prochaine action et signature du devis', module: 'Dossiers clients',
        titre: 'Chaque dossier vous dit quoi faire ensuite.',
        points: ['Quatre étapes : demande, devis et accord, passages, facturation.', 'Un encadré « Prochaine action » : relancer le client, faire signer sur place…', 'Le devis part avec un lien pour être signé en ligne.', 'Un clic pour revenir à l’étape précédente.'],
    },
];

/** Ordre de la page : le catalogue, le simulateur du client, ses dossiers, puis l'agenda. */
export const CAPTURES_PRESTATAIRE: CaptureMetier[] = [...PRESTATIONS, ...SIMULATEUR, ...DOSSIERS, ...AGENDA];
